import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import { formatDateOnly } from '../utils/date.ts';
import { assetTypeLabel } from '../utils/labels.ts';

interface Asset {
  id: string;
  description: string;
  type: string;
  acquisitionValue: number;
  amountPaid: number;
  currentValue: number;
  acquisitionDate: string;
  status: 'active' | 'inactive' | 'sold' | 'exchanged';
}

const safeNumber = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};


const toneFor = (s: string): 'green' | 'amber' | 'slate' | 'blue' | 'red' => {
  if (s === 'active') return 'green';
  if (s === 'sold') return 'slate';
  if (s === 'exchanged') return 'blue';
  return 'amber';
};

const Assets: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: assets, isLoading, error } = useQuery<Asset[]>(
    'assets',
    async () => (await api.get('/assets')).data,
    { staleTime: 1000 * 60 * 5 }
  );

  const filtered = useMemo(() => {
    return (assets || []).filter(a => {
      const q = searchTerm.toLowerCase();
      const matchesSearch = !q || a.description.toLowerCase().includes(q);
      const matchesType = filterType === 'all' || a.type === filterType;
      const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [assets, searchTerm, filterType, filterStatus]);

  const totalCurrent = filtered.reduce((s, a) => s + safeNumber(a.currentValue), 0);
  const totalAcquisition = filtered.reduce((s, a) => s + safeNumber(a.acquisitionValue), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar bens.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Bens e ativos"
        title="Bens e ativos"
        subtitle="Imóveis e demais bens"
        actions={
          hasPermission('assets.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/assets/novo')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo bem
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Tipo</span>
            <select className="select" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="all">Todos</option>
              <option value="real_estate">Imóvel</option>
              <option value="other">Outros</option>
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">Todos</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="sold">Vendido</option>
              <option value="exchanged">Permutado</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Descrição do bem"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Bens</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valor de aquisição</div>
          <div className="kpi-value">{formatCurrency(totalAcquisition)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valor atual</div>
          <div className="kpi-value">{formatCurrency(totalCurrent)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Bens</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum bem encontrado.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Bem</th>
                  <th className="hide-m">Tipo</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m">Aquisição</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Valor atual</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td>
                      <StackCell
                        title={a.description}
                        meta={`${assetTypeLabel(a.type)} · ${formatStatus(a.status)}`}
                        stackExtra={
                          <>
                            {formatCurrency(safeNumber(a.currentValue))}
                            {a.acquisitionDate ? ` · ${formatDateOnly(a.acquisitionDate)}` : ''}
                          </>
                        }
                      />
                    </td>
                    <td className="hide-m">{assetTypeLabel(a.type)}</td>
                    <td className="hide-m">
                      <StatusChip label={formatStatus(a.status)} tone={toneFor(a.status)} />
                    </td>
                    <td className="hide-m mono">
                      {a.acquisitionDate
                        ? formatDateOnly(a.acquisitionDate)
                        : '—'}
                    </td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(safeNumber(a.currentValue))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/assets/${a.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => {
                              setDeleteId(a.id);
                              setShowDeleteModal(true);
                            }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir Bem"
        message="Tem certeza que deseja excluir este bem? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await api.delete(`/assets/${deleteId}`);
            queryClient.invalidateQueries(['assets']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Assets;
